import React, { useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Typography from '@material-ui/core/Typography';
import { useSelector } from 'react-redux';

const COLORS = {
    btn: 'var(--md-btn-bg, #4caf50)',
    btnText: '#fff',
    listEvenBg: '#f0f1fb'
};

function displayValue(value) {
    return value === null || value === undefined ? '' : String(value);
}

function codeName(code, name) {
    const resolvedCode = displayValue(code).trim();
    const resolvedName = displayValue(name).trim();
    if (resolvedCode !== '' && resolvedName !== '') return `${resolvedCode}: ${resolvedName}`;
    return resolvedCode || resolvedName;
}

function ListPanel({ title, codeTitle, partnerTitle, items, codeField, nameField, partnerField, editable, headerStyle, onChange }) {
    return (
        <Box style={{ minWidth: 0, fontSize: 11 }}>
            <Typography variant="body2" style={{ marginBottom: 6, fontSize: '13px', fontFamily: 'inherit', fontWeight: 400 }}>
                {title}
            </Typography>
            <Box style={{ border: '1px solid #d0d0d0', borderRadius: 3, overflow: 'hidden' }}>
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '42px minmax(150px, 1fr) minmax(120px, 0.8fr)',
                        minHeight: 34,
                        alignItems: 'center',
                        ...headerStyle
                    }}
                >
                    <Box style={{ alignSelf: 'stretch', borderRight: '1px solid #d0d0d0' }} />
                    <Box style={{ padding: '6px 8px', fontWeight: 400, borderRight: '1px solid #d0d0d0' }}>{codeTitle}</Box>
                    <Box style={{ padding: '6px 8px', fontWeight: 400 }}>{partnerTitle}</Box>
                </Box>
                <Box style={{ height: 360, overflowY: 'auto' }}>
                    {items.map((item, index) => (
                        <Box
                            key={`${displayValue(item[codeField])}-${index}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '42px minmax(150px, 1fr) minmax(120px, 0.8fr)',
                                minHeight: 36,
                                alignItems: 'center',
                                borderTop: index === 0 ? 'none' : '1px solid #e0e0e0',
                                backgroundColor: index % 2 === 0 ? '#fff' : COLORS.listEvenBg
                            }}
                        >
                            <Checkbox
                                checked={Boolean(item.selected)}
                                disabled={!editable}
                                onChange={event => onChange(index, event.target.checked)}
                                color="default"
                                size="small"
                                style={{ padding: 7 }}
                            />
                            <Box style={{ padding: '6px 8px', overflowWrap: 'anywhere' }}>
                                {codeName(item[codeField], item[nameField])}
                            </Box>
                            <Box style={{ padding: '6px 8px', overflowWrap: 'anywhere' }}>
                                {displayValue(item[partnerField])}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

export default function SwatchExhibitionColorSizeDialog(props) {
    const themestyle = useSelector(state => state.form.themestyle);
    const [colors, setColors] = useState([]);
    const [sizes, setSizes] = useState([]);
    const headerStyle = {
        backgroundColor: themestyle && themestyle.header ? themestyle.header.back : '#8ea0a0',
        color: themestyle && themestyle.header ? themestyle.header.color : '#fff'
    };
    const detail = props.detail || {};
    const itemCode = displayValue(detail.PDT_ITMCITEMCD_cd || detail.PDT_ITMCITEMCD).trim();
    const itemName = displayValue(detail.PDT_ITMCITEMCD_nm).trim();

    useEffect(() => {
        setColors([]);
        setSizes([]);
        if (!props.open) return undefined;

        let active = true;
        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios) return undefined;

        client.postaxios('Swatch/GetExhibitionColorSize', {
            exhibitionNo: props.exhibitionNo,
            ownerCode: props.ownerCode,
            brandCode: props.brandCode,
            oppiCode: props.oppiCode
        }, {
            onSuccess: function (data) {
                if (active) {
                    const response = data && data.data ? data.data : {};
                    setColors(Array.isArray(response.colors) ? response.colors : []);
                    setSizes(Array.isArray(response.sizes) ? response.sizes : []);
                }
                client.wait(false);
            }
        });

        return () => {
            active = false;
        };
    }, [props.open, props.fnc, props.exhibitionNo, props.ownerCode, props.brandCode, props.oppiCode]);

    function changeColor(index, selected) {
        setColors(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, selected: selected } : item));
    }

    function changeSize(index, selected) {
        setSizes(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, selected: selected } : item));
    }

    function save() {
        if (!props.editable) return;
        const client = props.fnc && props.fnc.current;
        if (!client || !client.postaxios) return;

        client.postaxios('Swatch/SaveExhibitionColorSize', {
            exhibitionNo: props.exhibitionNo,
            ownerCode: props.ownerCode,
            brandCode: props.brandCode,
            oppiCode: props.oppiCode,
            selectedColorCodes: colors.filter(item => item.selected).map(item => item.colorCode),
            selectedSizeCodes: sizes.filter(item => item.selected).map(item => item.sizeCode)
        }, {
            onSuccess: function () {
                client.success();
                props.onClose();
                client.wait(false);
            }
        });
    }

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            maxWidth="md"
            fullWidth={true}
            aria-labelledby="swatch-exhibition-color-size-dialog-title"
        >
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <DialogTitle id="swatch-exhibition-color-size-dialog-title" disableTypography={true}>
                    <Typography variant="h6" style={{ fontSize: '15px', fontFamily: 'inherit', fontWeight: 400 }}>
                        カラー・サイズ基本情報
                    </Typography>
                </DialogTitle>
                <DialogActions style={{ paddingRight: 24 }}>
                    {props.editable && (
                        <Button variant="contained" onClick={save} style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }}>
                            登録
                        </Button>
                    )}
                    <Button variant="contained" onClick={props.onClose} style={{ backgroundColor: COLORS.btn, color: COLORS.btnText }}>
                        閉じる
                    </Button>
                </DialogActions>
            </Box>
            <DialogContent dividers={true} style={{ padding: 20 }}>
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '110px minmax(100px, 0.8fr) 70px minmax(100px, 0.8fr) 180px minmax(140px, 1.2fr)',
                        borderTop: '1px solid #d0d0d0',
                        borderLeft: '1px solid #d0d0d0',
                        marginBottom: 18,
                        fontSize: 11
                    }}
                >
                    {[
                        ['ブランド品番', displayValue(detail.SWT_HINBAN)],
                        ['PS品番', displayValue(detail.SWT_OPPICD)],
                        ['ブランドサブアイテム', codeName(itemCode, itemName)]
                    ].map(([label, value]) => (
                        <React.Fragment key={label}>
                            <Box style={{ ...headerStyle, padding: '7px 9px', borderRight: '1px solid #d0d0d0', borderBottom: '1px solid #d0d0d0', fontWeight: 400 }}>
                                {label}
                            </Box>
                            <Box style={{ padding: '7px 9px', borderRight: '1px solid #d0d0d0', borderBottom: '1px solid #d0d0d0', backgroundColor: '#eee', fontWeight: 400 }}>
                                {value}
                            </Box>
                        </React.Fragment>
                    ))}
                </Box>
                <Box style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
                    <ListPanel
                        title="カラー情報"
                        codeTitle="カラー"
                        partnerTitle="先方カラー"
                        items={colors}
                        codeField="colorCode"
                        nameField="colorName"
                        partnerField="senpoColor"
                        editable={props.editable}
                        headerStyle={headerStyle}
                        onChange={changeColor}
                    />
                    <ListPanel
                        title="サイズ情報"
                        codeTitle="サイズ"
                        partnerTitle="先方サイズ"
                        items={sizes}
                        codeField="sizeCode"
                        nameField="sizeName"
                        partnerField="senpoSize"
                        editable={props.editable}
                        headerStyle={headerStyle}
                        onChange={changeSize}
                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
}
